import { create } from 'zustand'

type Theme = 'light' | 'dark' | 'system'

interface ThemeState {
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return getSystemTheme()
  return theme
}

function applyTheme(resolved: 'light' | 'dark') {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? '#0a0c10' : '#f5f2ec')
  }
}

const stored = localStorage.getItem('pwg-theme') as Theme | null
const initialTheme: Theme = stored || 'dark'
const initialResolved = resolveTheme(initialTheme)
applyTheme(initialResolved)

export const useThemeStore = create<ThemeState>((set) => ({
  theme: initialTheme,
  resolvedTheme: initialResolved,
  setTheme: (theme: Theme) => {
    const resolved = resolveTheme(theme)
    localStorage.setItem('pwg-theme', theme)
    applyTheme(resolved)
    set({ theme, resolvedTheme: resolved })
  },
}))

// Listen for system theme changes when in 'system' mode
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
  const state = useThemeStore.getState()
  if (state.theme === 'system') {
    const resolved = e.matches ? 'dark' : 'light'
    applyTheme(resolved)
    useThemeStore.setState({ resolvedTheme: resolved })
  }
})
